/**
 * Geocoding Service - Converts addresses to GPS coordinates using Google Maps API
 * Based on the logic from ProjectSummary.vue
 */

/**
 * Geocodes an address using Google Maps Geocoding API
 * @param {Object} addressData - Object containing address components
 * @param {string} addressData.address_line_1 - Street address
 * @param {string} addressData.city - City name
 * @param {string} addressData.state - State abbreviation
 * @param {string} addressData.zipcode - ZIP code
 * @returns {Promise} Promise that resolves with {latitude, longitude} or rejects with error
 */
export function geocodeAddress(addressData) {
  return new Promise((resolve, reject) => {
    // Check if Google Maps API is available
    if (typeof google === 'undefined' || !google.maps || !google.maps.Geocoder) {
      reject(new Error('Google Maps API not available'));
      return;
    }

    // Build address string from address components
    const addressParts = [
      addressData.address_line_1,
      addressData.city,
      addressData.state,
      addressData.zipcode
    ].filter(part => part && part.trim() !== '');

    if (addressParts.length === 0) {
      reject(new Error('No address components provided'));
      return;
    }

    const fullAddress = addressParts.join(', ');
    console.log('🌍 Geocoding address:', fullAddress);

    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address: fullAddress }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const location = results[0].geometry.location;
        const lat = location.lat();
        const lng = location.lng();
        
        console.log('✅ Geocoding successful:', { lat, lng });
        
        resolve({
          latitude: lat,
          longitude: lng,
          formatted_address: results[0].formatted_address
        });
      } else {
        console.error('❌ Geocoding failed:', status);
        reject(new Error(`Geocoding failed: ${status}`));
      }
    });
  });
}

/**
 * Geocodes address data and adds latitude/longitude to the general_info object
 * @param {Object} generalInfo - Project general info object (will be modified)
 * @returns {Promise} Promise that resolves when geocoding is complete
 */
export async function addGpsCoordinates(generalInfo) {
  try {
    const coordinates = await geocodeAddress(generalInfo);
    
    // Add GPS coordinates to the general_info object
    generalInfo.latitude = coordinates.latitude;
    generalInfo.longitude = coordinates.longitude;
    
    console.log('📍 GPS coordinates added to project:', {
      latitude: coordinates.latitude,
      longitude: coordinates.longitude
    });
    
    return coordinates;
  } catch (error) {
    console.warn('⚠️ Geocoding failed, proceeding without GPS coordinates:', error.message);
    // Don't throw error - just proceed without coordinates
    return null;
  }
} 